/**
 * Authoritative Server-Side Checkout & Payment Verification Service Layer.
 * All order creations and payment verifications MUST be handled server-side.
 * Client-side order creation and unverified order fallbacks are strictly prohibited.
 */

import { supabase } from "@/lib/supabase";
import { products } from "@/lib/products";
import { checkIsCustomerBlocked } from "@/features/admin/customers/store/admin-customers-store";
import { calculateOrderPricing } from "@/lib/pricing-calculator";
import { getStoredShippingSettings } from "@/lib/store-settings";
import { apiUrl, readJsonResponse } from "@/lib/api-endpoints";

export interface ShippingAddress {
  fullName: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  email?: string;
}

export interface CreateOrderPayload {
  items: { id: string; qty: number; name?: string; price?: number }[];
  shippingAddress?: ShippingAddress;
  userEmail?: string;
  userPhone?: string;
  userId?: string;
  shippingMethod?: "standard" | "express";
}

export interface RazorpayOrderResponse {
  success: boolean;
  razorpayOrderId: string;
  keyId: string;
  currency: string;
  amount: number;
  orderSummary: {
    subtotal: number;
    shippingFee: number;
    gstAmount: number;
    total: number;
    items: { id: string; name: string; price: number; qty: number; image?: string }[];
  };
}

export interface VerifyPaymentPayload {
  razorpay_order_id?: string;
  razorpay_payment_id?: string;
  razorpay_signature?: string;
  userId?: string;
  userEmail?: string;
  userPhone?: string;
  items: { id: string; name: string; price: number; qty: number; image?: string }[];
  shippingAddress: ShippingAddress;
  shippingMethod?: "standard" | "express";
}

/**
 * 1. Create authoritative Razorpay order via serverless backend.
 * Discards client prices and calculates authoritative amounts server-side.
 */
export async function createServerRazorpayOrder(
  payload: CreateOrderPayload,
): Promise<RazorpayOrderResponse> {
  // Authoritative block check
  const blockCheck = await checkIsCustomerBlocked({
    email: payload.userEmail || payload.shippingAddress?.email,
    phone: payload.userPhone || payload.shippingAddress?.phone,
    userId: payload.userId,
  });

  if (blockCheck.isBlocked) {
    throw new Error(blockCheck.reason || "Your account is suspended. Purchases are disabled.");
  }

  const res = await fetch(apiUrl("create-razorpay-order"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (res.ok) {
    return await readJsonResponse<RazorpayOrderResponse>(res, "create-razorpay-order");
  }

  const errData = await res.json().catch(() => ({}));
  throw new Error(errData.error || `Server order creation failed (${res.status})`);
}

/**
 * 2. Verify payment signature & process order atomically on server.
 * Never creates an order client-side on failure.
 */
export async function verifyServerPayment(payload: VerifyPaymentPayload): Promise<{
  success: boolean;
  orderId: string;
  status: string;
  total?: number;
  message?: string;
  whatsappOrderUrl?: string;
}> {
  // Attach the session token when one exists so the server can bind the order to the
  // verified account rather than the body-supplied identity fields (which it now only
  // trusts when they match this token). Guests have no session and are recorded as guest
  // orders exactly as before.
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;

  const res = await fetch(apiUrl("verify-razorpay-payment"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (res.ok) {
    return await readJsonResponse<Awaited<ReturnType<typeof verifyServerPayment>>>(
      res,
      "verify-razorpay-payment",
    );
  }

  const errData = await res.json().catch(() => ({}));
  throw new Error(
    errData.error || `Payment verification failed (${res.status}). Payment was not confirmed.`,
  );
}

/**
 * 3. Revalidate cart prices and live stock availability against server.
 */
export interface RevalidateCartLine {
  id?: string;
  qty?: number;
  name?: string;
  product?: { id?: string };
}

export interface RevalidateCartResponse {
  valid: boolean;
  subtotal: number;
  shippingFee: number;
  gstAmount: number;
  total: number;
  warnings: string[];
}

interface RevalidateDbProduct {
  id: string;
  name: string;
  price: number;
  stock?: number | null;
  stock_quantity?: number | null;
  image?: string;
}

export async function revalidateCartAgainstServer(
  items: RevalidateCartLine[]
) {
  try {
    const res = await fetch(apiUrl("revalidate-cart"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });

    if (res.ok) {
      return await readJsonResponse<RevalidateCartResponse>(res, "revalidate-cart");
    }
  } catch {
    // Network fallback to live Supabase query
  }

  // Fallback: Direct read-only check against public catalog
  const productIds = items.map((i) => String(i.id || i.product?.id));
  let dbProds: RevalidateDbProduct[] = [];
  try {
    const { data } = await supabase
      .from("products")
      .select("id, name, price, stock, image")
      .in("id", productIds);
    if (data && data.length > 0) {
      dbProds = data;
    }
  } catch {
    // Fall back to seed catalog for display
  }

  if (dbProds.length === 0) {
    dbProds = (
      products as unknown as (RevalidateDbProduct & {
        stockQuantity?: number;
        inStock?: boolean;
      })[]
    )
      .filter((p) => productIds.includes(p.id))
      .map((p) => ({
        id: p.id,
        name: p.name,
        price: p.price,
        stock: p.stock ?? p.stockQuantity ?? (p.inStock ? 50 : 0),
        image: p.image,
      }));
  }

  const productMap = new Map((dbProds || []).map((p) => [p.id, p]));
  const pricingItems: { price: number; qty: number }[] = [];
  const warnings: string[] = [];

  for (const line of items) {
    const pId = String(line.id || line.product?.id);
    const dbP = productMap.get(pId);
    const qty = Number(line.qty || 1);
    if (!dbP) {
      warnings.push(`Item '${line.name || pId}' is no longer available.`);
      continue;
    }
    const availableStock = Number(dbP.stock ?? dbP.stock_quantity ?? 50);
    if (availableStock < qty) {
      warnings.push(`Only ${availableStock} units available for '${dbP.name}'.`);
    }
    pricingItems.push({
      price: Number(dbP.price),
      qty,
    });
  }

  const shippingConfig = getStoredShippingSettings();
  const pricing = calculateOrderPricing(pricingItems, {
    config: {
      standardShippingFee: shippingConfig.standardShippingFee,
      expressShippingFee: shippingConfig.expressShippingFee,
      freeShippingThreshold: shippingConfig.freeShippingThreshold,
    },
  });

  return {
    valid: warnings.length === 0,
    subtotal: pricing.subtotal,
    shippingFee: pricing.shippingFee,
    gstAmount: pricing.gstAmount,
    total: pricing.finalTotal,
    warnings,
  };
}
